/**
 * Soft prompt for guests: sign in to save / claim workspace.
 * Also snapshots Redux layers to localStorage for claim-guest after login.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Collapse, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import { useAppSelector } from '../store/hooks';
import { layersToGuestPayload, saveGuestLayersSnapshot } from '../services/guestWorkspaceService';
import { ensureGuestToken } from '../utils/sessionManager';
import { trackEvent } from '../services/analyticsService';

const DISMISS_KEY = 'geovera_guest_prompt_dismissed';

export default function GuestSavePrompt() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const layers = useAppSelector((s) => s.layers.layers);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setOpen(false);
      return;
    }
    ensureGuestToken();
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') {
        setOpen(false);
        return;
      }
    } catch {
      /* ignore */
    }
    // Show after a short delay so map can load first
    const t = window.setTimeout(() => {
      setOpen(true);
      trackEvent('signup_prompt_show');
    }, 2500);
    return () => window.clearTimeout(t);
  }, [isAuthenticated]);

  // Persist guest layers whenever they change (for claim after login)
  useEffect(() => {
    if (isAuthenticated) return;
    if (!layers?.length) return;
    const payload = layersToGuestPayload(
      layers.map((l) => ({
        id: l.id,
        name: l.name,
        geometryType: l.geometryType,
        crs: l.crs,
        symbology: (l.symbology as Record<string, unknown> | undefined) ?? null,
        features: l.features?.map((f) => ({
          geometry: f.geometry as { type: string; coordinates: unknown },
          properties: f.properties as Record<string, unknown>,
        })),
      }))
    );
    if (payload.length) saveGuestLayersSnapshot(payload);
  }, [layers, isAuthenticated]);

  if (isAuthenticated || !open) return null;

  const redirect = `${location.pathname}${location.search || ''}`;

  return (
    <Collapse in={open}>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 12, sm: 24 },
          left: { xs: 12, sm: 'auto' },
          zIndex: 1400,
          maxWidth: 380,
          p: 2,
          borderRadius: 3,
          bgcolor: 'rgba(11, 19, 43, 0.96)',
          border: '1px solid rgba(96, 165, 250, 0.35)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box>
            <Typography sx={{ color: '#F8FAFC', fontWeight: 800, fontSize: 14, mb: 0.5 }}>
              Sign in free to save your map
            </Typography>
            <Typography sx={{ color: '#94A3B8', fontSize: 12, lineHeight: 1.45 }}>
              Full WebGIS stays free forever. Sign in to claim guest work to your account and keep
              share links.
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Dismiss"
            onClick={() => {
              setOpen(false);
              try {
                localStorage.setItem(DISMISS_KEY, '1');
              } catch {
                /* ignore */
              }
            }}
            sx={{ color: '#94A3B8' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={() => {
            trackEvent('signup_click', { from: 'guest_save_prompt' });
            navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
          }}
          sx={{
            mt: 1.5,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#3B82F6',
            '&:hover': { bgcolor: '#2563EB' },
          }}
        >
          Sign in free with Google
        </Button>
      </Box>
    </Collapse>
  );
}
